// Publish Scheduler Component
import React, { useState } from 'react';
import { Calendar, Clock, CheckCircle } from 'lucide-react';
import { SocialPost, SocialPlatform } from '../types/social.types';
import { useSocialPosts } from '../hooks/useSocialPosts';

interface Props {
  post: SocialPost;
  platforms: SocialPlatform[];
  onSchedule: (date: Date) => void;
}

const PublishScheduler: React.FC<Props> = ({ post, platforms, onSchedule }) => {
  const { schedulePost } = useSocialPosts();
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [timezone, setTimezone] = useState('Europe/Warsaw');
  const [isScheduling, setIsScheduling] = useState(false);

  // Najlepsze czasy publikacji
  const bestTimes = {
    facebook: [
      { time: '09:00', label: 'Poranek (9:00)', engagement: 'wysokie' },
      { time: '13:00', label: 'Lunch (13:00)', engagement: 'średnie' },
      { time: '19:00', label: 'Wieczór (19:00)', engagement: 'bardzo wysokie' }
    ],
    linkedin: [
      { time: '07:30', label: 'Przed pracą (7:30)', engagement: 'wysokie' },
      { time: '12:00', label: 'Lunch (12:00)', engagement: 'bardzo wysokie' },
      { time: '17:00', label: 'Po pracy (17:00)', engagement: 'średnie' }
    ],
    instagram: [
      { time: '11:00', label: 'Przedpołudnie (11:00)', engagement: 'wysokie' },
      { time: '14:00', label: 'Popołudnie (14:00)', engagement: 'średnie' },
      { time: '20:00', label: 'Wieczór (20:00)', engagement: 'bardzo wysokie' }
    ],
    twitter: [
      { time: '08:00', label: 'Poranek (8:00)', engagement: 'wysokie' },
      { time: '12:00', label: 'Południe (12:00)', engagement: 'średnie' },
      { time: '17:00', label: 'Po pracy (17:00)', engagement: 'wysokie' }
    ]
  };

  const handleSchedule = async () => {
    if (!selectedDate || !selectedTime) {
      alert('Wybierz datę i godzinę publikacji');
      return;
    }

    setIsScheduling(true);
    try {
      const scheduledDate = new Date(`${selectedDate}T${selectedTime}`);
      await schedulePost(post.id, scheduledDate);
      onSchedule(scheduledDate);
      alert('Post został zaplanowany!');
    } catch (error) {
      console.error('Error scheduling post:', error);
      alert('Błąd podczas planowania posta');
    } finally {
      setIsScheduling(false);
    }
  };

  const getRecommendedTimes = () => {
    const recommendations = new Map<string, { count: number; engagement: string }>();
    
    platforms.forEach(platform => {
      const times = bestTimes[platform as keyof typeof bestTimes] || [];
      times.forEach(timeSlot => {
        const existing = recommendations.get(timeSlot.time) || { count: 0, engagement: timeSlot.engagement };
        recommendations.set(timeSlot.time, {
          count: existing.count + 1,
          engagement: existing.engagement
        });
      });
    });

    return Array.from(recommendations.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 3);
  };

  const recommendedTimes = getRecommendedTimes();
  const minDate = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">
          ℹ️ Zaplanuj publikację
        </h3>
        <p className="text-sm text-blue-700">
          Wybierz najlepszy czas publikacji dla maksymalnego zasięgu.
          Post zostanie automatycznie opublikowany na wybranych platformach.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Wybór daty i czasu */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline w-4 h-4 mr-1" />
              Data publikacji
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              min={minDate}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="inline w-4 h-4 mr-1" />
              Godzina publikacji
            </label>
            <input
              type="time"
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Strefa czasowa
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="Europe/Warsaw">Europa/Warszawa (CET)</option>
              <option value="UTC">UTC</option>
              <option value="America/New_York">Ameryka/Nowy Jork (EST)</option>
            </select>
          </div>
        </div>

        {/* Rekomendacje */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            💡 Rekomendowane godziny publikacji
          </h3>
          
          <div className="space-y-3">
            {recommendedTimes.map(([time, data]) => (
              <button
                key={time}
                onClick={() => setSelectedTime(time)}
                className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                  selectedTime === time
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{time}</p>
                    <p className="text-sm text-gray-600">
                      Rekomendowane dla {data.count} platform(y)
                    </p>
                  </div>
                  <span className={`text-sm px-2 py-1 rounded ${
                    data.engagement === 'bardzo wysokie'
                      ? 'bg-green-100 text-green-700'
                      : data.engagement === 'wysokie'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {data.engagement} zaangażowanie
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Szczegóły dla platform */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs font-medium text-gray-600 mb-2">
              Najlepsze czasy dla Twoich platform:
            </p>
            {platforms.map(platform => (
              <div key={platform} className="text-xs text-gray-500 mb-1">
                <span className="capitalize font-medium">{platform}:</span>{' '}
                {bestTimes[platform as keyof typeof bestTimes]?.map(t => t.time).join(', ')}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Podsumowanie */}
      {selectedDate && selectedTime && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3" />
            <div>
              <p className="font-medium text-green-900">
                Post zostanie opublikowany:
              </p>
              <p className="text-green-700">
                {new Date(`${selectedDate}T${selectedTime}`).toLocaleString('pl-PL', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              <p className="text-sm text-green-600 mt-1">
                na platformach: {platforms.join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Przyciski akcji */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          onClick={() => onSchedule(new Date())}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Anuluj
        </button>
        <button
          onClick={handleSchedule}
          disabled={!selectedDate || !selectedTime || isScheduling}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isScheduling ? 'Planowanie...' : 'Zaplanuj publikację'}
        </button>
      </div>
    </div>
  );
};

export default PublishScheduler;
